import { groupBy } from "lodash-es";
import { defineItemStore, type ItemStoreEvents } from "../itemStore.store";
import { sceneNodeStoreHooks } from "./SceneNodeStoreHooks";
import { splitUid, type Uid } from "@/helpers/utility/Id";
import { isInList, type Subset } from "@/helpers/utility/Type";
import { usePresenceStore } from "@editor/store/presence.store";
import { useSceneStore } from "./scenes.store";
import { getHighestParentNodes } from "./sceneNodeFunctions";
import { getOrderValuesBetween } from "@/helpers/utility/Sorting";
import { isSceneLayerNode } from "../sceneLayer/sceneLayerFunctions";
import { isSceneGroupNode } from "../sceneGroup/sceneGroupFunctions";
import { useSelectionStore } from "@editor/store/editor/selection.store";
import type { SceneNodeItem } from "./SceneNodeItem";
import type { SceneNodeData, SceneNodeType } from "./SceneNodeData";

export const sceneNodeItemStructure: SceneNodeItem = {
  type: "sceneNode",
  id: 0,
  uid: "sceneNode_0",

  sceneId: 0,
  parentSceneNodeUid: null,
  order: 0,

  dataType: "group",
  data: {} as any,

  x: 0,
  y: 0,
  width: 100,
  height: 100,
  rotation: 0,
  visible: true,
  opacity: 1,
  locked: false,

  title: null,

  // STYLE (all undefined by default)
  textAlign: null,
  textColor: null,
  fontSizeRelative: 1,
  fontFamily: null,
  lineHeight: null,
  imageId: null,
  aspectRatioLocked: undefined,
  fill: null,
  stroke: null,
  strokeWidth: null,
  strokeStyle: null,
  gridLocked: false,

  createdAt: 0,
};

const GRID_SIZE = 10;

// Add this type near the top of the file
type SceneNodeCreationData<T extends SceneNodeType> = Subset<SceneNodeItem> & {
  dataType: T;
  data: SceneNodeData<T>;
};

export const useSceneNodesStore = defineItemStore("model/sceneNodes", sceneNodeItemStructure, {
  actions: {
    async create<T extends SceneNodeType>(
      newNodeData: SceneNodeCreationData<T>,
      extraData?: object
    ): Promise<SceneNodeItem<T>> {
      // Set sceneId to the active scene if not provided
      const sceneStore = useSceneStore();
      const presence = usePresenceStore();
      let sceneId = newNodeData.sceneId ?? presence.activeSceneId;
      const scene = sceneStore.items[sceneId];
      if (!scene) {
        const scenes = sceneStore.orderedItems;
        if (scenes.length > 0) {
          sceneId = scenes[0].id;
          presence.set("activeSceneId", sceneId);
        } else {
          throw new Error(`Scene with ID ${sceneId} not found.`);
        }
      }
      newNodeData.sceneId = sceneId;

      // Set parentSceneNodeUid to the active layer if not provided
      const sceneNodes = this.byScene[sceneId] || [];
      if (newNodeData.dataType === "layer") {
        newNodeData.parentSceneNodeUid = null;
      } else {
        const activeLayerUid = newNodeData.parentSceneNodeUid ?? presence.activeSceneLayers[sceneId] ?? null;
        let activeLayer: SceneNodeItem | null = this.byUid[activeLayerUid ?? ""];
        if (!activeLayer || !isSceneLayerNode(activeLayer) && !isSceneGroupNode(activeLayer)) {
          activeLayer = this.getTopSceneLayer(sceneId);
        }
        if (activeLayer && isSceneLayerNode(activeLayer) && activeLayer.data.layerType === "foreground") {
          // The foreground layer can't contain "decorative" nodes, so switch to the top scene layer
          if (isInList(newNodeData.dataType, ["medium", "chart", "text", "shape"])) {
            activeLayer = this.getTopSceneLayer(sceneId);
          }
        }
        if (!activeLayer) {
          // Still no active layer? That means that there's none.
          // In this case, create the layer first!
          const newLayer = await this.create({
            sceneId,
            dataType: "layer",
            data: {
              layerType: "scene"
            }
          });
          activeLayer = newLayer;
        }
        newNodeData.parentSceneNodeUid = activeLayer?.uid ?? null;
      }

      // Set order to be last in the parent if not provided
      const siblingNodes = sceneNodes.filter(
        (node) => node.parentSceneNodeUid === newNodeData.parentSceneNodeUid
      );
      const maxOrder = siblingNodes.reduce((max, node) => (node.order > max ? node.order : max), 0);
      newNodeData.order = newNodeData.order ?? maxOrder + 100;

      console.log("Creating scene node with data:", newNodeData);

      return this.super.create(newNodeData as SceneNodeCreationData<T>, extraData) as Promise<SceneNodeItem<T>>;
    },

    async duplicate(nodeId: number, changes: Subset<SceneNodeItem> = {}) {
      const node = this.items[nodeId];
      const duplicate = await this.super.duplicate(nodeId, { ...changes });

      // Duplicate child nodes recursively
      const childNodes = this.byParentSceneNodeUid[node.uid] || [];
      for (const childNode of childNodes) {
        await this.duplicate(childNode.id, { sceneId: duplicate.sceneId, parentSceneNodeUid: duplicate.uid });
      }

      return duplicate;
    },
    
    updateDataKey<T extends SceneNodeType>(
      nodeId: number,
      key: keyof SceneNodeData<T>,
      value: SceneNodeData<T>[typeof key]
    ) {
      const node = this.items[nodeId] as SceneNodeItem<T>;
      if (!node) throw new Error(`SceneNode with ID ${nodeId} not found`);
      
      const updatedData = {
        ...node.data,
        [key]: value
      } as SceneNodeData<T>;

      return this.update({
        id: nodeId,
        data: updatedData
      });
    },

    updateData<T extends SceneNodeType>(
      nodeId: number,
      newData: Partial<SceneNodeData<T>>
    ) {
      const node = this.items[nodeId] as SceneNodeItem<T>;
      if (!node) throw new Error(`SceneNode with ID ${nodeId} not found`);

      const updatedData = {
        ...node.data,
        ...newData
      } as SceneNodeData<T>;

      return this.update({
        id: nodeId,
        data: updatedData
      });
    },

    async group(nodeUids: Uid<"sceneNode">[], options: Subset<SceneNodeItem> = {}) {
      if (nodeUids.length === 0) {
        throw new Error("No nodes provided to group.");
      }

      const nodes = nodeUids.map((uid) => this.byUid[uid]).filter(Boolean).filter((n) => n.dataType !== "layer").sort((a, b) => a.order - b.order);

      // Find group placement
      const parentNodes = getHighestParentNodes(nodes);
      if (parentNodes.length === 0) {
        throw new Error("No valid parent node to place group.");
      }
      const parentNode = parentNodes[0];
      const parentChildren = nodes.filter((n) => n.parentSceneNodeUid === parentNode.uid);
      const minOrder = Math.min(...parentChildren.map((n) => n.order));

      // create the group node
      const groupNode = await this.create({
        dataType: "group",
        data: {},
        parentSceneNodeUid: parentNode.uid,
        order: minOrder, 
        ...options
      });

      // update each node to be a child of the new group
      let orderIndex = 0;
      for (const node of nodes) {
        this.update({
          id: node.id,
          parentSceneNodeUid: groupNode.uid,
          order: orderIndex += 100,
        });
      }

      return groupNode;
    },

    async ungroup(groupNodeId: { type: "sceneNode", id: number } | { uid: Uid<"sceneNode"> }) {
      const id = getId(groupNodeId);
      const groupNode = this.items[id];
      if (!groupNode) throw new Error(`SceneNode ${id} not found`);
      const nextNode = this.getNextSiblingNode(groupNode);

      const children = this.byParentSceneNodeUid[groupNode.uid] || [];
      const newChildIndices = getOrderValuesBetween(groupNode.order, nextNode?.order, children.length);
      let index = 0;
      for (const child of children) {
        this.update({
          id: child.id,
          parentSceneNodeUid: groupNode.parentSceneNodeUid,
          order: newChildIndices[index++],
        });
      }

      if (groupNode.dataType === "group") {
        await this.delete({ id: groupNode.id });
      }
    },

    async moveToBack(nodeID: { type: "sceneNode", id: number } | { uid: Uid<"sceneNode"> }) {
      const id = getId(nodeID);
      const node = this.items[id];
      if (!node) throw new Error(`SceneNode ${id} not found`);

      const siblings = this.byParentSceneNodeUid[node.parentSceneNodeUid ?? "root"] || [];
      const minOrder = Math.min(...siblings.map((n) => n.order));

      this.update({
        id: node.id,
        order: minOrder - 100,
      });
    },

    async moveToFront(nodeID: { type: "sceneNode", id: number } | { uid: Uid<"sceneNode"> }) {
      const id = getId(nodeID);
      const node = this.items[id];
      if (!node) throw new Error(`SceneNode ${id} not found`);

      const siblings = this.byParentSceneNodeUid[node.parentSceneNodeUid ?? "root"] || [];
      const maxOrder = Math.max(...siblings.map((n) => n.order));

      this.update({
        id: node.id,
        order: maxOrder + 100,
      });
    }
  },
  getters: {
    byScene(): { [sceneId: number]: SceneNodeItem[] } {
      return groupBy(this.items, (item) => item.sceneId);
    },
    byNodeType(): { [key in SceneNodeType]: SceneNodeItem<key>[] } {
      return groupBy(this.items, (item) => item.dataType) as { [key in SceneNodeType]: SceneNodeItem<key>[] };
    },
    byParentSceneNodeUid(): { [parentSceneNodeUid: string]: SceneNodeItem[] } {
      return groupBy(this.items, (item) => item.parentSceneNodeUid ?? "root");
    },
    getByParentSceneNodeUid(): {
      (parentSceneNodeUid: Uid<"sceneNode"> | null): SceneNodeItem[];
    } {
      return (parentSceneNodeUid: Uid<"sceneNode"> | null) => {
        return Object.values(this.items).filter(item => item.parentSceneNodeUid === parentSceneNodeUid);
      };
    },
    getPrevSiblingNode(): {
      (node: SceneNodeItem): SceneNodeItem | null;
    } {
      return (node: SceneNodeItem) => {
        const siblings = this.byParentSceneNodeUid[node.parentSceneNodeUid ?? "root"] || [];
        const sortedSiblings = siblings.sort((a, b) => a.order - b.order);
        const index = sortedSiblings.findIndex(sibling => sibling.id === node.id);
        if (index > 0) {
          return sortedSiblings[index - 1];
        }
        return null;
      }
    },
    getNextSiblingNode(): {
      (node: SceneNodeItem): SceneNodeItem | null;
    } {
      return (node: SceneNodeItem) => {
        const siblings = this.byParentSceneNodeUid[node.parentSceneNodeUid ?? "root"] || [];
        const sortedSiblings = siblings.sort((a, b) => a.order - b.order);
        const index = sortedSiblings.findIndex(sibling => sibling.id === node.id);
        if (index >= 0 && index < sortedSiblings.length - 1) {
          return sortedSiblings[index + 1];
        }
        return null;
      }
    },
    getTopSceneLayer(): {
      (sceneId: number): SceneNodeItem<"layer"> | null;
    } {
      return (sceneId: number) => {
        const sceneNodes = this.byScene[sceneId] || [];
        const layerNodes = sceneNodes.filter((node) => isSceneLayerNode(node));
        if (layerNodes.length === 0) return null;
        const sceneLayerNodes = layerNodes.filter((node) => node.data.layerType === "scene");
        if (sceneLayerNodes.length === 0) return null;
        const sorted = sceneLayerNodes.sort((a, b) => b.order - a.order);
        return sorted[0];
      }
    }
  },
  events: {
    async beforeCreate(newNode) {
      if (!newNode.dataType) throw new Error("SceneNode must have a dataType");
      const hook = sceneNodeStoreHooks[newNode.dataType].beforeCreate;
      if (hook == null) return newNode;
      return (hook as NonNullable<ItemStoreEvents<SceneNodeItem>["beforeCreate"]>).call(this, newNode);
    },
    async created(newNode, store) {
      const hook = sceneNodeStoreHooks[newNode.dataType].created;
      if (hook == null) return;
      return (hook as NonNullable<ItemStoreEvents<SceneNodeItem>["created"]>).call(this, newNode, store);
    },
    beforeUpdate(updatedNode, updatedData, oldNode) {
      const extraChanges: Partial<SceneNodeItem> = {};
      const hook = sceneNodeStoreHooks[updatedNode.dataType].beforeUpdate;
      if (hook) {
        const hookChanges = (hook as NonNullable<ItemStoreEvents<SceneNodeItem>["beforeUpdate"]>).call(this, updatedNode, { ...updatedData, ...extraChanges }, oldNode) || {};
        Object.assign(extraChanges, hookChanges);
      }
      if (updatedNode.gridLocked || updatedData.gridLocked || isLockedToGrid(updatedNode.dataType)) {
        // If the node type is locked to grid, round the x and y values to the nearest integer
        if ("x" in updatedData && updatedData.x != null) {
          updatedNode.x = Math.round(updatedData.x / GRID_SIZE) * GRID_SIZE;
        }
        if ("y" in updatedData && updatedData.y != null) {
          updatedNode.y = Math.round(updatedData.y / GRID_SIZE) * GRID_SIZE;
        }
      }
      return extraChanges;
    },
    async afterUpdate(updatedNode, updatedData, oldNode) {
      const hook = sceneNodeStoreHooks[updatedNode.dataType].afterUpdate;
      if (hook == null) return;
      return (hook as NonNullable<ItemStoreEvents<SceneNodeItem>["afterUpdate"]>).call(this, updatedNode, updatedData, oldNode);
    },
    async beforeDuplicate(duplicatedNode, changes, sourceNode) {
      const hook = sceneNodeStoreHooks[sourceNode.dataType].beforeDuplicate;
      if (hook == null) return changes;
      return (await (hook as NonNullable<ItemStoreEvents<SceneNodeItem>["beforeDuplicate"]>).call(this, duplicatedNode, changes, sourceNode)) || changes;
    },
    async afterDelete(deletedNode, store, extraOptions) {
      const hook = sceneNodeStoreHooks[deletedNode.dataType].afterDelete;
      const selectionStore = useSelectionStore();
      selectionStore.remove(deletedNode);
      if (hook == null) return;
      return (hook as NonNullable<ItemStoreEvents<SceneNodeItem>["afterDelete"]>).call(this, deletedNode, store, extraOptions);
    },
    async canBeDeleted(deletedNode, store, extraOptions) {
      const hook = sceneNodeStoreHooks[deletedNode.dataType].canBeDeleted;
      if (hook == null) return true;
      return (hook as NonNullable<ItemStoreEvents<SceneNodeItem>["canBeDeleted"]>).call(this, deletedNode, store, extraOptions);
    }
  }
});

function getId(item: { id: number } | { uid: Uid<"sceneNode"> }): number {
  if ("id" in item) {
    return item.id;
  } else if ("uid" in item) {
    return splitUid(item.uid).id;
  }
  throw new Error("Item must have either 'id' or 'uid'");
}

function isLockedToGrid(type: SceneNodeType): boolean {
  return isInList(type, ["element"]);
}
